// src/components/BulkZoneDialog.tsx - FIXED VERSION
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Alert,
  OutlinedInput,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import api from '../services/api';

interface VehicleGeofenceStatus {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  geofencingEnabled: boolean;
  zoneCount: number;
}

interface BulkZoneDialogProps {
  open: boolean;
  onClose: () => void;
  vehicles: VehicleGeofenceStatus[];
  onSuccess: () => void;
}

const BulkZoneDialog: React.FC<BulkZoneDialogProps> = ({
  open,
  onClose,
  vehicles,
  onSuccess
}) => {
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [zoneData, setZoneData] = useState<any>({
    name: '',
    type: 'circular',
    radius: 500,
    rules: {
      alertOnEntry: true,
      alertOnExit: true,
      alertOnBreach: true,
      speedLimit: 60
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVehicleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedVehicles(typeof value === 'string' ? value.split(',') : value);
  };

  const handleApply = async () => {
    if (selectedVehicles.length === 0) {
      setError('Please select at least one vehicle');
      return;
    }

    if (!zoneData.name) {
      setError('Zone name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/geofencing/zones/bulk-apply', {
        vehicleIds: selectedVehicles,
        zoneData
      });

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply zones');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Apply Zone to Multiple Vehicles</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
          <InputLabel>Select Vehicles</InputLabel>
          <Select
            multiple
            value={selectedVehicles}
            onChange={handleVehicleChange}
            input={<OutlinedInput label="Select Vehicles" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const vehicle = vehicles.find(v => v._id === value);
                  return (
                    <Chip key={value} label={vehicle?.plateNumber} size="small" />
                  );
                })}
              </Box>
            )}
          >
            {vehicles.map((vehicle) => (
              <MenuItem key={vehicle._id} value={vehicle._id}>
                <Checkbox checked={selectedVehicles.indexOf(vehicle._id) > -1} />
                <ListItemText 
                  primary={vehicle.plateNumber} 
                  secondary={`${vehicle.make} ${vehicle.model}`} 
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Zone configuration */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Zone Name</InputLabel>
          <OutlinedInput
            value={zoneData.name}
            onChange={(e) => setZoneData({ ...zoneData, name: e.target.value })}
            label="Zone Name"
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" disabled={loading}>
          {loading ? 'Applying...' : `Apply to ${selectedVehicles.length} Vehicles`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkZoneDialog;